import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { CoachingRealtimeService } from '../../services/coaching-realtime.service';
import {
  CoachingService,
  InboxMode,
  InboxThread,
  productLineLabel
} from '../../services/coaching.service';

const REFRESH_MS = 60000;
const MOBILE_BP = 768;

/**
 * Global unread coaching conversations for admins / trainers.
 * Desktop: right dock. Mobile: FAB + expandable panel.
 */
@Component({
  selector: 'app-coaching-unread-dock',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './coaching-unread-dock.component.html',
  styleUrls: ['./coaching-unread-dock.component.scss']
})
export class CoachingUnreadDockComponent implements OnInit, OnDestroy {
  threads: InboxThread[] = [];
  unreadTotal = 0;
  loading = false;
  collapsed = false;
  mobileOpen = false;
  isMobile = false;
  hideForRoute = false;
  isCoachViewer = false;
  mode: InboxMode = 'admin';

  private destroy$ = new Subject<void>();
  readonly productLineLabel = productLineLabel;

  constructor(
    private auth: AuthService,
    private coaching: CoachingService,
    private realtime: CoachingRealtimeService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.updateViewport();
    try {
      this.collapsed = localStorage.getItem('coachUnreadDockCollapsed') === 'true';
    } catch { /* ignore */ }

    this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.resolveViewer();
      if (user && this.isCoachViewer) {
        this.refresh();
      } else {
        this.threads = [];
        this.unreadTotal = 0;
      }
    });

    this.realtime.messages$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (!this.isCoachViewer || this.hideForRoute) {
        return;
      }
      if (event.message.from_user) {
        this.refresh();
      }
    });

    this.realtime.coachUnreadTotal$.pipe(takeUntil(this.destroy$)).subscribe(n => {
      this.unreadTotal = n;
    });

    interval(REFRESH_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isCoachViewer && !document.hidden && !this.hideForRoute) {
        this.refresh(true);
      }
    });

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(e => this.updateRouteVisibility(e.urlAfterRedirects));
    this.updateRouteVisibility(this.router.url);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  updateViewport(): void {
    this.isMobile = window.innerWidth < MOBILE_BP;
  }

  get showShell(): boolean {
    return this.isCoachViewer && !this.hideForRoute;
  }

  get showFab(): boolean {
    return this.showShell && this.isMobile;
  }

  get showStrip(): boolean {
    return this.showShell && !this.isMobile && this.collapsed;
  }

  get showPanel(): boolean {
    if (!this.showShell) {
      return false;
    }
    if (this.isMobile) {
      return this.mobileOpen;
    }
    return !this.collapsed;
  }

  toggle(): void {
    if (this.isMobile) {
      this.mobileOpen = !this.mobileOpen;
      if (this.mobileOpen) {
        this.refresh();
      }
      return;
    }
    this.collapsed = !this.collapsed;
    try {
      localStorage.setItem('coachUnreadDockCollapsed', String(this.collapsed));
    } catch { /* ignore */ }
    if (!this.collapsed) {
      this.refresh();
    }
  }

  close(): void {
    if (this.isMobile) {
      this.mobileOpen = false;
      return;
    }
    this.collapsed = true;
    try {
      localStorage.setItem('coachUnreadDockCollapsed', 'true');
    } catch { /* ignore */ }
  }

  refresh(silent = false): void {
    if (!this.isCoachViewer) {
      return;
    }
    if (!silent) {
      this.loading = true;
    }
    this.coaching.listThreads(this.mode, { scope: 'mine', unread: true, page: 1, limit: 40 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.threads = res.items;
          this.unreadTotal = res.items.reduce((sum, t) => sum + (t.unread_count || 0), 0);
          this.loading = false;
        },
        error: () => { this.loading = false; }
      });
  }

  openThread(threadId: string): void {
    const path = this.mode === 'admin' ? '/manage/coaching' : '/coach-console/inbox';
    this.router.navigate([path], { queryParams: { thread: threadId, tab: this.mode === 'admin' ? 'chats' : undefined } });
    if (this.isMobile) {
      this.mobileOpen = false;
    }
  }

  initials(person?: { first_name?: string; last_name?: string; name?: string } | null): string {
    if (!person) {
      return '?';
    }
    const parts = person.name ? person.name.split(' ') : [person.first_name || '', person.last_name || ''];
    return parts.filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';
  }

  trackById(_: number, item: InboxThread): string {
    return item.thread_id;
  }

  private resolveViewer(): void {
    const roles = this.auth.getRoles().map(r => String(r).toUpperCase());
    const isAdmin = roles.includes('ADMIN');
    const isTrainer = roles.includes('TRAINER');
    this.isCoachViewer = isAdmin || isTrainer;
    this.mode = isAdmin ? 'admin' : 'coach';
  }

  private updateRouteVisibility(url: string): void {
    // Keep available on coaching pages too — user asked for global, including while coaching.
    // Hide on auth screens and the post-checkout pages (they'd cover the confirmation card).
    this.hideForRoute = url.startsWith('/auth') || url.startsWith('/subscription');
  }
}
